// ============================================================================
// ClientActivitiesPanel — the trainer's view of one client's activity_log.
// ----------------------------------------------------------------------------
// Two things in one panel:
//   • a "Suggest homework" action (opens SuggestHomeworkForm → POST /activities)
//   • the list of this client's activities (GET /api/activities?client_id=),
//     split into trainer HOMEWORK (source='suggested', violet) and the client's
//     SELF-LOGGED exercise (source='own', lime) — so the trainer can see what
//     the client did on their own. Live-synced: a client logging an exercise
//     broadcasts `activity`, which the live-sync layer maps to an invalidation
//     of the activities root, refreshing this list. Mobile-first, Pulse, EN+繁中.
// ============================================================================
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardHeader, EmptyState } from "../../components";
import { IconPlus } from "../../components/icons";
import { useClientActivities } from "../../hooks/activities";
import type { Activity } from "../../api";
import { LoadingBlock } from "../../app/PageState";
import { SuggestHomeworkForm } from "./SuggestHomeworkForm";
import { fmtDate } from "./lib";
import { ENTRY_STYLE } from "../../dashboard/sessionStyle";
import { cn } from "../../lib/cn";

export function ClientActivitiesPanel({ clientId }: { clientId: number }) {
  const { t } = useTranslation("activity");
  const query = useClientActivities(clientId);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const { homework, own } = useMemo(() => {
    const all = query.data ?? [];
    const at = (a: Activity) =>
      a.performed_at ?? a.scheduled_at ?? a.created_at ?? "";
    const hw = all
      .filter((a) => a.source === "suggested")
      .sort((a, b) => at(b).localeCompare(at(a)));
    const ow = all
      .filter((a) => a.source === "own")
      .sort((a, b) => at(b).localeCompare(at(a)));
    return { homework: hw, own: ow };
  }, [query.data]);

  return (
    <>
      <Card>
        <CardHeader
          title={t("trainer.logged.title")}
          subtitle={t("trainer.logged.subtitle")}
          action={
            <Button
              size="sm"
              onClick={() => setSuggestOpen(true)}
              iconLeft={<IconPlus width={16} height={16} />}
            >
              {t("trainer.suggest.action")}
            </Button>
          }
        />

        {query.isLoading ? (
          <LoadingBlock />
        ) : homework.length === 0 && own.length === 0 ? (
          <EmptyState
            title={t("trainer.logged.empty")}
            action={
              <Button
                size="sm"
                onClick={() => setSuggestOpen(true)}
                iconLeft={<IconPlus width={16} height={16} />}
              >
                {t("trainer.suggest.action")}
              </Button>
            }
            className="!py-12"
          />
        ) : (
          <div className="space-y-5">
            {homework.length > 0 && (
              <Group title={t("trainer.logged.suggestedTag")}>
                {homework.slice(0, 12).map((a, i) => (
                  <ActivityRow key={a.id} activity={a} index={i} kind="activity_suggested" />
                ))}
              </Group>
            )}
            {own.length > 0 && (
              <Group title={t("trainer.logged.ownTag")}>
                {own.slice(0, 12).map((a, i) => (
                  <ActivityRow key={a.id} activity={a} index={i} kind="activity_own" />
                ))}
              </Group>
            )}
          </div>
        )}
      </Card>

      <SuggestHomeworkForm
        clientId={clientId}
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
      />
    </>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
        {title}
      </p>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function ActivityRow({
  activity,
  index,
  kind,
}: {
  activity: Activity;
  index: number;
  kind: "activity_own" | "activity_suggested";
}) {
  const { t } = useTranslation("activity");
  const reduce = useReducedMotion();
  const st = ENTRY_STYLE[kind];
  const when = activity.performed_at ?? activity.scheduled_at;
  const isDone = activity.status === "completed";

  const metricBits: string[] = [];
  if (activity.sets != null) metricBits.push(t("detail.setsValue", { count: activity.sets }));
  if (activity.reps != null) metricBits.push(t("detail.repsValue", { count: activity.reps }));
  if (activity.weight_kg != null) metricBits.push(t("detail.weightValue", { weight: activity.weight_kg }));
  if (activity.duration_min != null)
    metricBits.push(t("detail.minutesValue", { count: activity.duration_min }));

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: reduce ? 0 : Math.min(index * 0.04, 0.3) }}
    >
      <div className="flex items-center gap-3 rounded-md bg-surface p-3 ring-1 ring-line">
        <span className={cn("h-10 w-1.5 shrink-0 rounded-pill", st.grad)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {activity.title || activity.type || t("type.fallback")}
            </span>
            <Badge tone={st.tone} className="shrink-0">
              {t(`provenance.${kind}`)}
            </Badge>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-body-soft">
            {when && <span className="tabular-nums">{fmtDate(when)}</span>}
            {activity.type && <span className="truncate">{activity.type}</span>}
            {metricBits.length > 0 && <span>{metricBits.join(" · ")}</span>}
          </div>
        </div>
        <Badge tone={isDone ? "success" : "neutral"} dot className="shrink-0">
          {isDone ? t("detail.statusCompleted") : t("detail.statusPlanned")}
        </Badge>
      </div>
    </motion.li>
  );
}
